import { ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type {
  EnpsOverallMetric,
  EnpsComponentMetric,
} from '@/types/dashboard.types';
import { DateRange } from '@/types/dashboard.types';
import { BarChart } from './bar-chart';
import {
  getChangeColorClass,
  getChangeLabel,
  getChartLabel,
  getValueSublabel,
} from './analytics-card-helpers';

interface EnpsMetricCardProps {
  title: string;
  metric: EnpsOverallMetric | EnpsComponentMetric;
  dateRange: DateRange;
  onNavigate: () => void;
  showPercentSymbol?: boolean;
  // For driver dashboard special case (shows "Last eNPS Response" card)
  isDriverDashboard?: boolean;
  // For Driver eNPS card with trendline (non-driver dashboards)
  showTrendline?: boolean;
  tooltip?: string;
}

export function EnpsMetricCard({
  title,
  metric,
  dateRange,
  onNavigate,
  showPercentSymbol = false,
  isDriverDashboard = false,
  showTrendline = false,
  tooltip,
}: EnpsMetricCardProps) {
  const icon = tooltip ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="analytics-card-external-link" onClick={onNavigate}>
            <ExternalLink />
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    <div className="analytics-card-external-link" onClick={onNavigate}>
      <ExternalLink />
    </div>
  );

  // For Driver eNPS card with trendline (non-driver dashboards)
  if (showTrendline) {
    const enpsMetric = metric as EnpsOverallMetric;
    const trendline = enpsMetric.trendline || [];
    const periodLabel = enpsMetric.trendlinePeriodLabel;

    const hasChange = enpsMetric.absoluteChange !== undefined;
    const isIncrease = enpsMetric.isIncrease ?? false;
    const isZeroChange = enpsMetric.absoluteChange === 0;
    const changeColor = getChangeColorClass(isIncrease, isZeroChange);

    const cardContent = (
      <Card className="analytics-card">
        <CardContent className="analytics-card-content">
          {icon}
          <div className="analytics-card-title">{title}</div>
          <div className="analytics-card-value-container">
            <span className="analytics-card-value">{enpsMetric.value}</span>
            {hasChange && (
              <div className={`analytics-card-change-row ${changeColor}`}>
                <span className="analytics-card-change-value">
                  {isZeroChange || isIncrease ? '↑' : '↓'}{' '}
                  {enpsMetric.absoluteChange}
                </span>
                <span className="analytics-card-change-label">
                  {getChangeLabel(dateRange)}
                </span>
              </div>
            )}
          </div>
          <div className="analytics-card-sublabel">
            {getValueSublabel(dateRange, 'score')}
          </div>
          <BarChart
            trendline={trendline}
            label={getChartLabel(dateRange, periodLabel, trendline, 'scores')}
            scaleType="enps"
            dateRange={dateRange}
            periodLabel={periodLabel}
            averageValue={enpsMetric.value}
          />
        </CardContent>
      </Card>
    );

    return cardContent;
  }

  // For component metrics (Promoters/Passives/Detractors/Driver Last eNPS)
  const componentMetric = metric as EnpsComponentMetric;
  const change = componentMetric.absoluteChange;
  const isIncrease = componentMetric.isIncrease;
  const isZeroChange = change === 0;
  const changeColor = getChangeColorClass(isIncrease, isZeroChange);

  // Determine change label based on dashboard type
  const changeLabel = isDriverDashboard
    ? 'since previous response'
    : getChangeLabel(dateRange);

  const cardContent = (
    <Card className="analytics-card">
      <CardContent className="analytics-card-content">
        {icon}
        <div className="analytics-card-title">{title}</div>
        <div className="analytics-card-value-container">
          <span className="analytics-card-value">
            {componentMetric.value}
            {showPercentSymbol && '%'}
          </span>
          <div className={`analytics-card-change-row ${changeColor}`}>
            <span className="analytics-card-change-value">
              {isZeroChange || isIncrease ? '↑' : '↓'} {Math.abs(change)}
              {!isDriverDashboard && ' pp'}
            </span>
            <span className="analytics-card-change-label">{changeLabel}</span>
          </div>
        </div>
        <div className="analytics-card-sublabel">
          {isDriverDashboard ? ' ' : getValueSublabel(dateRange, 'percentage')}
        </div>
        {isDriverDashboard ? (
          <BarChart
            trendline={componentMetric.trendline || []}
            label={getChartLabel(
              dateRange,
              componentMetric.trendlinePeriodLabel,
              componentMetric.trendline || [],
              'scores',
            )}
            scaleType="enps-response"
            dateRange={dateRange}
            periodLabel={componentMetric.trendlinePeriodLabel}
          />
        ) : (
          <BarChart
            trendline={componentMetric.trendline || []}
            label={getChartLabel(
              dateRange,
              componentMetric.trendlinePeriodLabel,
              componentMetric.trendline || [],
              'percentages',
            )}
            scaleType="percentage"
            dateRange={dateRange}
            periodLabel={componentMetric.trendlinePeriodLabel}
            averageValue={componentMetric.value}
          />
        )}
      </CardContent>
    </Card>
  );

  return cardContent;
}
